import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { StudentNetworkEvent, type CamelCasedStudentNetworkEvent } from 'StudentNetworkEvent';
import { useEventLogger } from 'FrontRoyalAngular/useEventLogger';
import { navigationHelper } from 'NavigationHelpers';
import { DashboardCard } from 'DashboardCard';
import { useOfflineModeManager } from 'FrontRoyalAngular';
import { useEventsForEventList } from './useEventsForEventList';
import { EventListEntry } from './EventListEntry';
import { ExpandableEventGroup } from './ExpandableEventGroup';

type Props = {
    featured?: boolean;
};

type PresentationProps = {
    orderedEventTitles: string[];
    eventsByTitle: Record<string, CamelCasedStudentNetworkEvent[]>;
    featured: boolean;
    handleViewAllPress: () => void;
    handleEventSelect: (networkEvent: CamelCasedStudentNetworkEvent) => void;
    isLoading: boolean;
};

const getEventTypeAndPayload = (networkEvent?: CamelCasedStudentNetworkEvent, featured?: boolean) => {
    if (networkEvent) return ['clicked_view_event_in_network', { student_network_event_id: networkEvent.id }] as const;

    if (!featured) return ['clicked_view_all_events_in_network', {}] as const;

    return ['clicked_view_featured_events_in_network', {}] as const;
};

export function EventList({ featured = false }: Props) {
    const EventLogger = useEventLogger();
    const { loadRoute } = navigationHelper();
    const { orderedEventTitles, eventsByTitle, isLoading } = useEventsForEventList({ featured });

    const logAndNavigateToEvents = useCallback(
        (networkEvent?: CamelCasedStudentNetworkEvent) => {
            EventLogger.log('student-dashboard:navigate-to-student-network', {
                student_network_event_id: networkEvent?.id,
            });

            const [eventType, payload] = getEventTypeAndPayload(networkEvent, featured);
            EventLogger.log(eventType, payload);

            loadRoute(networkEvent?.id ? `/student-network/events/${networkEvent.id}` : '/student-network/events');
        },
        [featured, EventLogger, loadRoute],
    );

    const handleEventSelect = useCallback(
        (networkEvent: CamelCasedStudentNetworkEvent) => {
            logAndNavigateToEvents(networkEvent);
        },
        [logAndNavigateToEvents],
    );

    return (
        <EventListPresentation
            orderedEventTitles={orderedEventTitles}
            eventsByTitle={eventsByTitle}
            featured={featured}
            handleViewAllPress={logAndNavigateToEvents}
            handleEventSelect={handleEventSelect}
            isLoading={isLoading}
        />
    );
}

// We set the card padding to 0 so that the hover styles and borders between list items
// will stretch all the way to the edges
const CARD_PADDING = 0;

export function EventListPresentation({
    orderedEventTitles,
    eventsByTitle,
    featured,
    handleViewAllPress,
    handleEventSelect,
    isLoading,
}: PresentationProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'lessons.stream.featured_events' });
    const headingText = featured ? t('featured_events') : t('upcoming_events');
    const OfflineModeManager = useOfflineModeManager();

    if (OfflineModeManager.inOfflineMode) return null;
    if (!isLoading && orderedEventTitles.length === 0) return null;

    return (
        <DashboardCard
            headingText={headingText}
            handleViewAllPress={handleViewAllPress}
            isLoading={isLoading}
            cardPadding={CARD_PADDING}
        >
            {orderedEventTitles.map(eventTitle => {
                const events = eventsByTitle[eventTitle];
                const isSingleEvent = events.length === 1;

                return (
                    <EventListEntry key={events[0].id}>
                        {isSingleEvent ? (
                            <StudentNetworkEvent event={events[0]} onClick={() => handleEventSelect(events[0])} />
                        ) : (
                            <ExpandableEventGroup events={events} handleEventSelect={handleEventSelect} />
                        )}
                    </EventListEntry>
                );
            })}
        </DashboardCard>
    );
}
